import LockIcon from "@uui/Lock03";
import TrashIcon from "@uui/Trash01";
import { toLower } from "lodash/fp";

import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";

import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import type {
  CustomVocabulary,
  PatchCustomVocabularyBody,
} from "@app/analytics/types";
import useAuth from "@app/common/auth/useAuth";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import type { User } from "@app/common/types";
import { isWorkspaceAdmin } from "@app/common/workspaces/workspaceUtils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import DropdownMenuItem from "@rui/Dropdown/DropdownMenuItem";
import IconBackground from "@rui/IconBackground";
import Menu from "@rui/Menu";
import { Span } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  CUSTOM_VOCABULARY_TYPE_CONFIG,
  CUSTOM_VOCABULARY_TYPES,
} from "./constants";
import {
  CustomVocabularyChip,
  StyledTableRow,
  TableContainer,
  TypeSelectorButton,
  TypeSelectorLabel,
} from "./styles";

const HEAD_CELLS: HeadCell<CustomVocabulary>[] = [
  {
    id: "word",
    label: "Word",
    sortBy: (w) => toLower(w.word),
  },
  {
    id: "type",
    label: "Type",
  },
  {
    id: "createdOn",
    label: "Date added",
    sortBy: (w) => w.createdOn.toMillis(),
  },
  {
    id: "addedByName",
    label: "Added by",
    sortBy: (w) => w.addedByName ?? "",
  },
  {
    id: "actions",
    label: "",
    sortable: false,
  },
];

const CustomVocabularyTableRow = ({
  currentUserId,
  isWorkspaceAdmin,
  onDeleteWord,
  onUpdateWord,
  row,
}: {
  currentUserId?: User["id"];
  isWorkspaceAdmin: boolean;
  onDeleteWord: (id: CustomVocabulary["id"]) => void;
  onUpdateWord: (updatedWord: PatchCustomVocabularyBody) => void;
  row: CustomVocabulary;
}) => {
  const { open, onClick, onClose, anchorElement } = useAnchorElement();

  const canEditWord =
    isWorkspaceAdmin ||
    (row.addedByUserId != null && row.addedByUserId == currentUserId);

  const { Icon: TypeIcon, label: typeLabel } =
    CUSTOM_VOCABULARY_TYPE_CONFIG[row.type];

  const handleOpenTypeSelectorMenu: typeof onClick = (event) => {
    event.stopPropagation();
    onClick(event);
  };

  const handleChangeType = (newType: CustomVocabulary["type"]) => () => {
    if (!canEditWord) return;
    onClose();
    onUpdateWord({
      id: row.id,
      type: newType,
    });
  };

  return (
    <>
      <StyledTableRow>
        <TableCell>
          <CustomVocabularyChip>{row.word}</CustomVocabularyChip>
        </TableCell>
        <TableCell>
          <TypeSelectorButton
            startIcon={<TypeIcon />}
            disabled={!canEditWord}
            onClick={handleOpenTypeSelectorMenu}
          >
            {typeLabel}
          </TypeSelectorButton>
        </TableCell>
        <TableCell>
          {row.createdOn.toLocaleString({
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </TableCell>
        <TableCell>
          {row.addedByName ? (
            <>{row.addedByName}</>
          ) : (
            <Span sx={{ fontStyle: "italic", color: "shades.neutral.500" }}>
              Deleted user
            </Span>
          )}
        </TableCell>
        <TableCell width="40px">
          {canEditWord ? (
            <IconButton
              color="error"
              sx={{ height: "20px", width: "20px" }}
              onClick={() => onDeleteWord(row.id)}
            >
              <TrashIcon sx={{ fontSize: "14px" }} />
            </IconButton>
          ) : (
            <TooltipWrapper title="Only admins can delete vocabulary added by other users">
              <IconBackground height={20} sx={{ bgcolor: "shades.neutral.50" }}>
                <LockIcon sx={{ color: "shades.neutral.300" }} />
              </IconBackground>
            </TooltipWrapper>
          )}
        </TableCell>
      </StyledTableRow>

      {canEditWord && (
        <Menu
          open={open}
          onClose={onClose}
          anchorEl={anchorElement}
          disablePadding
          sx={{
            "& .MuiList-root": {
              py: 1,
              minWidth: "180px",
              color: "shades.neutral.600",
            },
          }}
        >
          {CUSTOM_VOCABULARY_TYPES.map((config) => {
            const isSelected = config.value === row.type;
            return (
              <DropdownMenuItem
                key={config.value}
                label={
                  <TypeSelectorLabel config={config} selected={isSelected} />
                }
                onClick={handleChangeType(config.value)}
                selected={isSelected}
                value={config.value}
              />
            );
          })}
        </Menu>
      )}
    </>
  );
};

const CustomVocabularyTable = ({
  onDeleteWord,
  onUpdateWord,
  vocabulary,
}: {
  onDeleteWord: (id: CustomVocabulary["id"]) => void;
  onUpdateWord: (updatedWord: PatchCustomVocabularyBody) => void;
  vocabulary: CustomVocabulary[];
}) => {
  const { user } = useAuth();

  const { handleSort, order, orderBy, tableRows } = useDataTable(
    vocabulary,
    HEAD_CELLS,
    { defaultOrder: "asc" }
  );

  return (
    <TableContainer>
      <Table stickyHeader>
        <TableHead
          headCells={HEAD_CELLS}
          onSort={handleSort}
          order={order}
          orderBy={orderBy}
          sx={{
            "& .MuiTableRow-root .MuiTableCell-root": {
              padding: "4px 8px",
            },
          }}
        />
        <TableBody>
          {tableRows.map((row) => (
            <CustomVocabularyTableRow
              key={row.id}
              currentUserId={user?.id}
              isWorkspaceAdmin={isWorkspaceAdmin(user?.workspace)}
              onDeleteWord={onDeleteWord}
              onUpdateWord={onUpdateWord}
              row={row}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomVocabularyTable;
